'use client';

import React, { useEffect } from 'react';
import { selectSavingsAmount } from '@/app/store/use-offer-comparison-store';
import { animate, motion, useMotionValue, useTransform } from 'motion/react';
import useOfferComparisonStore from '@/app/store/use-offer-comparison-store';
import animationConfig from '@/lib/config/animation-config';
import { useTranslations } from 'next-intl';
import { ReferenceTariffType } from '../types/reference-tariff-type';
import { formatPrice } from '../utils/format-price';

export default function SavingsBanner() {
  const savings = useOfferComparisonStore(selectSavingsAmount);
  const savingsMotion = useMotionValue(0);
  const value = useTransform(() => formatPrice(savingsMotion.get(), false));
  const animationSpeed = animationConfig.speeds.accelerated;
  const tariffType = useOfferComparisonStore((state) => state.mode);
  const bannerType =
    tariffType == ReferenceTariffType.average ? 'average' : 'invoice';
  const t = useTranslations('savingsBanner.title');
  const savingsStyles =
    'bg-[var(--color-span-on-highlight)] text-[var(--color-text-strong)] font-heading px-2 py-3 md:py-1 rounded-lg my-3 md:my-0 text-center block md:inline-block';
  const bannerTitle = t.rich(bannerType, {
    highlight: (chunks) => (
      <span data-testid="savings-text" className={savingsStyles}>
        {chunks}
      </span>
    ),
    value: (chunks) => (
      <span data-testid="savingsBannerValue">
        <span data-testid="savings-value">
          {chunks}
          <motion.span>{value}</motion.span>
        </span>
      </span>
    ),
  });

  const savingsText =
    savings != null && savings > 0 ? (
      <>{bannerTitle}</>
    ) : (
      <span data-testid="no-savings-text"> {t('noSavings')}</span>
    );
  useEffect(() => {
    if (!savings) {
      return;
    }
    const controls = animate(savingsMotion, savings, {
      duration: animationSpeed,
    });
    return () => controls.stop();
  }, [animationSpeed, savings, savingsMotion]);

  if (savings == null) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{
        duration: 0.4,
        scale: { type: 'spring', visualDuration: 0.4, bounce: 0.3 },
      }}
    >
      <div
        className="bg-[var(--color-highlight-brand)] min-w-full rounded-3.75 text-[var(--color-text-inverse)] py-6 px-4 md:py-8 md:px-12 md:text-center"
        data-testid="savings-banner-root"
      >
        <p className="font-heading text-xl md:text-[40px] leading-7 md:leading-[55px]">
          {savingsText}
        </p>
      </div>
    </motion.div>
  );
}
